<section class="av-card p-4 d-flex flex-column gap-3">
  <div class="d-flex flex-wrap align-items-start justify-content-between gap-3">
    <div class="d-flex flex-column gap-1">
      <h2 class="av-section-title m-0">{{ 'backoffice.rbac.title' | translate }}</h2>
      <p class="av-meta m-0">{{ 'backoffice.rbac.read_only_note' | translate }}</p>
    </div>

    <button type="button" class="av-btn" (click)="facade.reload()">
      <mat-icon aria-hidden="true">refresh</mat-icon>
      {{ 'backoffice.common.actions.refresh' | translate }}
    </button>
  </div>

  <form
    [formGroup]="formGroup"
    class="d-flex flex-wrap align-items-end gap-3"
    (ngSubmit)="submit()"
  >
    <label class="filter-field d-flex flex-column gap-1" for="role-search">
      <span class="av-meta">{{ 'backoffice.rbac.search_label' | translate }}</span>
      <input
        id="role-search"
        type="search"
        class="av-field"
        formControlName="search"
        autocomplete="off"
        [placeholder]="'backoffice.rbac.search_placeholder' | translate"
      />
    </label>

    <div class="d-flex align-items-center gap-2">
      <button type="submit" class="av-btn av-btn-primary">
        <mat-icon aria-hidden="true">search</mat-icon>
        {{ 'backoffice.common.actions.search' | translate }}
      </button>

      <button type="button" class="av-btn" (click)="reset()">
        <mat-icon aria-hidden="true">filter_alt_off</mat-icon>
        {{ 'backoffice.common.actions.clear' | translate }}
      </button>
    </div>
  </form>
</section>

<section class="av-card p-4 mt-3 d-flex flex-column">
  @switch (facade.state()) {
    @case ('error') {
      <av-empty-state
        icon="error_outline"
        [title]="'backoffice.rbac.error.title' | translate"
        [message]="'backoffice.rbac.error.message' | translate"
      ></av-empty-state>
    }
    @case ('ready') {
      @if (facade.isEmpty()) {
        <av-empty-state
          icon="shield"
          [title]="'backoffice.rbac.empty.title' | translate"
          [message]="'backoffice.rbac.empty.message' | translate"
        ></av-empty-state>
      } @else {
        <div class="table-scroll">
          <table class="av-table w-100">
            <thead>
              <tr>
                <th scope="col">{{ 'backoffice.rbac.columns.role' | translate }}</th>
                <th scope="col">{{ 'backoffice.rbac.columns.organization' | translate }}</th>
                <th scope="col" class="text-end">
                  {{ 'backoffice.rbac.columns.permissions' | translate }}
                </th>
                <th scope="col" class="text-end">
                  {{ 'backoffice.rbac.columns.users' | translate }}
                </th>
                <th scope="col">{{ 'backoffice.rbac.columns.kind' | translate }}</th>
                <th scope="col" class="text-end">
                  {{ 'backoffice.rbac.columns.actions' | translate }}
                </th>
              </tr>
            </thead>

            <tbody>
              @for (role of facade.roles(); track role.id) {
                <tr>
                  <td class="cell-strong">{{ role.displayName }}</td>
                  <td class="cell-muted">{{ role.organizationLabel }}</td>
                  <td class="text-end">{{ role.permissions_count }}</td>
                  <td class="text-end">{{ role.users_count }}</td>
                  <td>
                    @if (role.is_read_only) {
                      <av-status-chip
                        [label]="'backoffice.rbac.read_only_chip' | translate"
                        tone="neutral"
                      ></av-status-chip>
                    } @else {
                      <av-status-chip
                        [label]="'backoffice.rbac.editable_chip' | translate"
                        tone="info"
                      ></av-status-chip>
                    }
                  </td>
                  <td class="text-end">
                    <button
                      type="button"
                      class="av-btn"
                      [matTooltip]="'backoffice.rbac.show_permissions' | translate"
                      (click)="selectRole(role.id)"
                    >
                      <mat-icon aria-hidden="true">
                        {{ facade.selectedRoleId() === role.id ? 'expand_less' : 'expand_more' }}
                      </mat-icon>
                    </button>
                  </td>
                </tr>

                @if (facade.selectedRoleId() === role.id) {
                  <tr>
                    <td colspan="6">
                      @if (facade.selectedRole(); as detail) {
                        <div class="d-flex flex-column gap-2">
                          <span class="av-meta">
                            {{ 'backoffice.rbac.scope_source_note' | translate }}
                          </span>

                          @if (detail.permissions.length === 0) {
                            <span class="av-section-hint">
                              {{ 'backoffice.rbac.grants_nothing' | translate }}
                            </span>
                          } @else {
                            <ul class="permission-list d-flex flex-wrap gap-2 m-0 p-0">
                              @for (permission of detail.permissions; track permission.id) {
                                <li
                                  class="permission-item d-flex align-items-center gap-2 px-2 py-1"
                                >
                                  <span>{{ permission.displayName }}</span>

                                  @if (permission.is_scopeable) {
                                    <av-status-chip
                                      [label]="'backoffice.rbac.scopeable_chip' | translate"
                                      tone="info"
                                    ></av-status-chip>
                                  }

                                  @if (permission.scope) {
                                    <span class="av-meta">{{ permission.scopeLabel }}</span>
                                  }
                                </li>
                              }
                            </ul>
                          }
                        </div>
                      } @else {
                        <span class="av-section-hint">
                          {{ 'backoffice.common.loading_inline' | translate }}
                        </span>
                      }
                    </td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>

        <av-paginator
          [page]="facade.page()"
          [pageSize]="facade.pageSize()"
          [total]="facade.total()"
          (pageChange)="goToPage($event)"
          (pageSizeChange)="changePageSize($event)"
        ></av-paginator>
      }
    }
    @default {
      <av-empty-state
        icon="hourglass_empty"
        [title]="'backoffice.rbac.loading.title' | translate"
        [message]="'backoffice.rbac.loading.message' | translate"
      ></av-empty-state>
    }
  }
</section>

<section class="av-card p-4 mt-3 d-flex flex-column gap-3">
  <div class="d-flex flex-column gap-1">
    <h2 class="av-section-title m-0">{{ 'backoffice.rbac.catalogue.title' | translate }}</h2>
    <p class="av-meta m-0">{{ 'backoffice.rbac.scope_source_note' | translate }}</p>
  </div>

  @if (facade.permissions().length === 0) {
    <av-empty-state
      icon="key_off"
      [title]="'backoffice.rbac.catalogue.empty_title' | translate"
      [message]="'backoffice.rbac.catalogue.empty_message' | translate"
    ></av-empty-state>
  } @else {
    <ul class="permission-list d-flex flex-wrap gap-2 m-0 p-0">
      @for (permission of facade.permissions(); track permission.id) {
        <li class="permission-item d-flex align-items-center gap-2 px-2 py-1">
          <span>{{ permission.displayName }}</span>

          @if (permission.is_scopeable) {
            <av-status-chip
              [label]="'backoffice.rbac.scopeable_chip' | translate"
              tone="info"
            ></av-status-chip>
          }
        </li>
      }
    </ul>
  }
</section>
